import { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Fish } from "lucide-react";

// ─── Ocean animation keyframes ───────────────────────────────────────────────
const OCEAN_CSS = `
  @keyframes oc-wave   { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
  @keyframes oc-bubble { 0% { transform: translateY(0) translateX(0); opacity: 0; }
                         8% { opacity: .75; } 90% { opacity: .45; }
                         100% { transform: translateY(-104vh) translateX(var(--bx, 12px)); opacity: 0; } }
  @keyframes oc-sway   { 0%, 100% { transform: rotate(-5deg); } 50% { transform: rotate(5deg); } }
  @keyframes oc-pulse  { 0%, 100% { transform: scaleY(1) scaleX(1); } 50% { transform: scaleY(.88) scaleX(1.04); } }
  @keyframes oc-ray    { 0%, 100% { opacity: .14; } 50% { opacity: .4; } }
  @keyframes oc-glow   { 0%, 100% { opacity: .55; } 50% { opacity: 1; } }
  .oc-sway  { animation: oc-sway 6s ease-in-out infinite; }
  .oc-pulse { animation: oc-pulse 3.4s ease-in-out infinite; transform-origin: top center; }
  .oc-glow  { animation: oc-glow 3.6s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) {
    .oc-sway, .oc-pulse, .oc-glow, .oc-bubble, .oc-ray, .oc-wave-track { animation: none !important; }
  }
`;

const OCEAN_GRADIENT =
  "linear-gradient(180deg, #7ff0e4 0%, #34d1d8 9%, #16b6cf 19%, #0e8fc0 33%, #0a66a6 49%, #073f7e 64%, #05285a 79%, #031a3c 91%, #020f28 100%)";

// ─── Sea creatures (inline SVG) ──────────────────────────────────────────────
const Clownfish = ({ size = 58 }) => (
  <svg width={size} height={size * 0.62} viewBox="0 0 64 40" style={{ filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.28))" }}>
    <path d="M2 20 L16 10 L16 30 Z" fill="#ff7a2f" />
    <ellipse cx="37" cy="20" rx="25" ry="13" fill="#ff8d3f" />
    <path d="M21 8 C18 20 21 32 21 32 L27 31 C24 20 27 9 27 9 Z" fill="#fff" />
    <path d="M41 9 C38 20 41 31 41 31 L47 30 C44 20 47 10 47 10 Z" fill="#fff" />
    <path d="M30 8 Q38 1 47 8" stroke="#ff7a2f" strokeWidth="3" fill="none" strokeLinecap="round" />
    <path d="M30 33 Q37 39 45 33" stroke="#ff7a2f" strokeWidth="3" fill="none" strokeLinecap="round" />
    <circle cx="53" cy="18" r="2.6" fill="#08263a" />
    <circle cx="54" cy="17" r="0.9" fill="#fff" />
  </svg>
);

const TangFish = ({ size = 50, color = "#3ad0e0" }) => (
  <svg width={size} height={size * 0.66} viewBox="0 0 60 40" style={{ filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.25))" }}>
    <path d="M3 20 L18 9 L18 31 Z" fill={color} />
    <ellipse cx="36" cy="20" rx="23" ry="12" fill={color} />
    <path d="M20 9 Q34 2 50 9" stroke={color} strokeWidth="3.5" fill="none" strokeLinecap="round" />
    <ellipse cx="36" cy="20" rx="23" ry="12" fill="#ffffff" opacity="0.14" />
    <circle cx="51" cy="18" r="2.3" fill="#06243a" />
  </svg>
);

const Jellyfish = ({ size = 46 }) => (
  <svg width={size} height={size * 1.34} viewBox="0 0 48 64" style={{ filter: "drop-shadow(0 0 16px rgba(255,120,160,0.5))" }}>
    <g className="oc-pulse">
      <path d="M4 26 C4 7 24 4 24 4 C24 4 44 7 44 26 C44 31 40 33 36 33 L12 33 C8 33 4 31 4 26 Z"
        fill="rgba(251,113,133,0.5)" stroke="rgba(255,212,224,0.85)" strokeWidth="1.3" />
      <ellipse cx="24" cy="19" rx="13" ry="8" fill="rgba(255,255,255,0.22)" />
    </g>
    {[10, 17, 24, 31, 38].map((x, i) => (
      <path key={x} d={`M${x} 33 q ${i % 2 ? -4 : 4} 10 0 20 q ${i % 2 ? 4 : -4} 8 0 11`}
        stroke="rgba(255,160,188,0.65)" fill="none" strokeWidth="2" strokeLinecap="round" />
    ))}
  </svg>
);

const Seahorse = ({ size = 44 }) => (
  <svg width={size} height={size * 1.42} viewBox="0 0 40 58" style={{ filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.28))" }}>
    <path d="M15 4 Q27 1 29 11 Q31 19 24 22 Q16 26 18 34 Q20 44 13 50 Q7 55 5 50 Q11 47 10 40 Q9 30 18 26 Q24 23 22 17 Q20 10 14 12 Q9 13 11 8 Q12 4 15 4 Z"
      fill="#ffc24b" stroke="#f0a93c" strokeWidth="1" />
    <path d="M29 7 Q34 9 31 13" stroke="#ff9d5c" strokeWidth="2.4" fill="none" strokeLinecap="round" />
    <circle cx="20" cy="10" r="1.7" fill="#5a3a12" />
  </svg>
);

const MiniSchool = ({ size = 30, color = "#9bf6d8" }) => (
  <div className="flex gap-1.5" style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.2))" }}>
    {[0, 1, 2].map((i) => (
      <Fish key={i} style={{ color, width: size, height: size, opacity: 0.85 - i * 0.12 }} />
    ))}
  </div>
);

// ─── Drifting wrapper ────────────────────────────────────────────────────────
const Drifter = ({ children, top, dur, delay = 0, dir = "right", bob = 14 }) => {
  const from = dir === "right" ? "-14vw" : "114vw";
  const to = dir === "right" ? "114vw" : "-14vw";
  return (
    <motion.div
      className="absolute"
      style={{ top, left: 0, willChange: "transform" }}
      initial={{ x: from }}
      animate={{ x: to, y: [0, -bob, bob * 0.6, -bob * 0.8, 0] }}
      transition={{
        x: { duration: dur, repeat: Infinity, ease: "linear", delay },
        y: { duration: dur / 4, repeat: Infinity, ease: "easeInOut" },
      }}
    >
      <div style={{ transform: dir === "left" ? "scaleX(-1)" : "none" }}>{children}</div>
    </motion.div>
  );
};

// ─── Bottom: seaweed + coral ─────────────────────────────────────────────────
const SeaweedBlade = ({ left, height, color, delay }) => (
  <div className="absolute bottom-0 oc-sway" style={{ left, transformOrigin: "bottom center", animationDelay: `${delay}s` }}>
    <svg width={height * 0.28} height={height} viewBox="0 0 24 100" preserveAspectRatio="none">
      <path d="M12 100 C3 76 21 64 12 46 C4 30 19 18 12 0" stroke={color} strokeWidth="6" fill="none" strokeLinecap="round" />
    </svg>
  </div>
);

const Coral = ({ left, scale = 1, color = "#ff7e9d" }) => (
  <div className="absolute bottom-0 oc-sway" style={{ left, transformOrigin: "bottom center", animationDelay: "-2s" }}>
    <svg width={70 * scale} height={84 * scale} viewBox="0 0 70 84">
      <g stroke={color} strokeWidth="7" fill="none" strokeLinecap="round">
        <path d="M35 84 L35 34" />
        <path d="M35 50 C24 42 20 30 22 18" />
        <path d="M35 44 C46 38 52 26 50 14" />
        <path d="M35 58 C28 52 24 44 25 36" />
        <path d="M35 52 C43 47 47 40 46 32" />
      </g>
      <circle cx="22" cy="16" r="5" fill={color} />
      <circle cx="50" cy="12" r="5" fill={color} />
      <circle cx="35" cy="32" r="5" fill={color} />
    </svg>
  </div>
);

// ─── The full ocean scene ────────────────────────────────────────────────────
export const OceanScene = () => {
  const reduce = useReducedMotion();

  const bubbles = useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => ({
        left: `${(i * 4.55 + (i % 3) * 6) % 100}%`,
        size: 5 + (i % 6) * 4,
        dur: 9 + (i % 7) * 2.2,
        delay: -(i * 1.4),
        drift: (i % 2 ? 1 : -1) * (8 + (i % 4) * 7),
      })),
    []
  );

  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <style>{OCEAN_CSS}</style>

      {/* Deep-to-shallow base gradient */}
      <div className="absolute inset-0" style={{ background: OCEAN_GRADIENT }} />

      {/* God rays — angled light beams from surface */}
      {[12, 30, 52, 74, 90].map((leftPct, i) => (
        <div
          key={i}
          className="oc-ray absolute top-0"
          style={{
            left: `${leftPct}%`,
            width: `${70 + (i % 3) * 40}px`,
            height: "62%",
            background: "linear-gradient(180deg, rgba(225,255,255,0.5), transparent)",
            transform: `skewX(${i % 2 ? -10 : 12}deg)`,
            filter: "blur(8px)",
            opacity: 0.22,
            animation: reduce ? "none" : `oc-ray ${7 + i}s ease-in-out ${i * 0.6}s infinite`,
          }}
        />
      ))}

      {/* Top contrast veil for hero legibility */}
      <div className="absolute inset-x-0 top-0 h-[420px]" style={{ background: "linear-gradient(180deg, rgba(3,26,52,0.34), transparent)" }} />

      {/* Surface waves */}
      <div className="absolute inset-x-0 top-0">
        <div className="oc-wave-track" style={{ width: "200%", animation: reduce ? "none" : "oc-wave 16s linear infinite" }}>
          <svg viewBox="0 0 1440 110" preserveAspectRatio="none" style={{ width: "50%", display: "inline-block" }}>
            <path d="M0,40 C180,80 320,8 540,38 C760,68 900,12 1120,40 C1280,60 1380,30 1440,36 L1440,0 L0,0 Z" fill="rgba(190,250,248,0.30)" />
          </svg>
          <svg viewBox="0 0 1440 110" preserveAspectRatio="none" style={{ width: "50%", display: "inline-block" }}>
            <path d="M0,40 C180,80 320,8 540,38 C760,68 900,12 1120,40 C1280,60 1380,30 1440,36 L1440,0 L0,0 Z" fill="rgba(190,250,248,0.30)" />
          </svg>
        </div>
        <div className="oc-wave-track" style={{ width: "200%", marginTop: -78, animation: reduce ? "none" : "oc-wave 24s linear infinite reverse" }}>
          <svg viewBox="0 0 1440 110" preserveAspectRatio="none" style={{ width: "50%", display: "inline-block" }}>
            <path d="M0,56 C220,18 360,86 600,52 C820,22 980,80 1200,50 C1320,34 1400,52 1440,48 L1440,0 L0,0 Z" fill="rgba(120,224,232,0.22)" />
          </svg>
          <svg viewBox="0 0 1440 110" preserveAspectRatio="none" style={{ width: "50%", display: "inline-block" }}>
            <path d="M0,56 C220,18 360,86 600,52 C820,22 980,80 1200,50 C1320,34 1400,52 1440,48 L1440,0 L0,0 Z" fill="rgba(120,224,232,0.22)" />
          </svg>
        </div>
      </div>

      {/* Swimming creatures */}
      {!reduce && (
        <>
          <Drifter top="14%" dur={30} dir="right" bob={16}><Clownfish size={60} /></Drifter>
          <Drifter top="22%" dur={44} dir="left" delay={6} bob={10}><TangFish size={46} color="#3ad0e0" /></Drifter>
          <Drifter top="38%" dur={52} dir="right" delay={3} bob={22}><Jellyfish size={50} /></Drifter>
          <Drifter top="34%" dur={26} dir="left" delay={1} bob={12}><MiniSchool size={26} color="#9bf6d8" /></Drifter>
          <Drifter top="56%" dur={38} dir="right" delay={8} bob={14}><TangFish size={54} color="#ffd34d" /></Drifter>
          <Drifter top="64%" dur={58} dir="left" delay={2} bob={18}><Jellyfish size={42} /></Drifter>
          <Drifter top="72%" dur={34} dir="right" delay={5} bob={12}><Seahorse size={46} /></Drifter>
          <Drifter top="82%" dur={42} dir="left" delay={4} bob={10}><Clownfish size={48} /></Drifter>
        </>
      )}

      {/* Rising bubbles — fixed so they keep rising as you dive */}
      <div className="fixed inset-0 overflow-hidden">
        {bubbles.map((b, i) => (
          <span
            key={i}
            className="oc-bubble absolute rounded-full"
            style={{
              left: b.left,
              bottom: "-24px",
              width: b.size,
              height: b.size,
              "--bx": `${b.drift}px`,
              background: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.9), rgba(190,245,255,0.18))",
              border: "1px solid rgba(255,255,255,0.35)",
              animation: reduce ? "none" : `oc-bubble ${b.dur}s linear ${b.delay}s infinite`,
              opacity: reduce ? 0 : undefined,
            }}
          />
        ))}
      </div>

      {/* Ocean floor: sand + seaweed + coral */}
      <div className="absolute inset-x-0 bottom-0">
        <div className="relative">
          <SeaweedBlade left="4%" height={150} color="#2fae7a" delay={0} />
          <SeaweedBlade left="9%" height={108} color="#3ec98c" delay={-1.5} />
          <Coral left="16%" scale={0.9} color="#ff7e9d" />
          <SeaweedBlade left="30%" height={130} color="#36bd82" delay={-0.8} />
          <Coral left="44%" scale={0.7} color="#ffa24b" />
          <SeaweedBlade left="58%" height={166} color="#2fae7a" delay={-2.2} />
          <SeaweedBlade left="64%" height={120} color="#46d196" delay={-0.4} />
          <Coral left="76%" scale={1} color="#ff6f9c" />
          <SeaweedBlade left="89%" height={138} color="#39c488" delay={-1.1} />
          <SeaweedBlade left="95%" height={100} color="#46d196" delay={-2.6} />

          <svg viewBox="0 0 1440 170" preserveAspectRatio="none" className="block w-full" style={{ height: 150 }}>
            <defs>
              <linearGradient id="oc-sand" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f0d79e" />
                <stop offset="55%" stopColor="#dcb877" />
                <stop offset="100%" stopColor="#b58f52" />
              </linearGradient>
            </defs>
            <path d="M0,86 C220,44 400,78 640,60 C880,42 1060,82 1300,58 C1360,52 1410,62 1440,58 L1440,170 L0,170 Z" fill="url(#oc-sand)" />
            <path d="M0,86 C220,44 400,78 640,60 C880,42 1060,82 1300,58 C1360,52 1410,62 1440,58"
              fill="none" stroke="rgba(255,247,224,0.55)" strokeWidth="2" />
          </svg>
        </div>
      </div>
    </div>
  );
};
